import { Box, Card, Flex, Grid, NumberInput, SegmentedControl, Text, TextInput } from '@mantine/core';
import DatePicker from '../DatePicker';
import LabeledSlider from '../LabeledSlider';

const modes = [
  { label: 'off', value: 'off' },
  { label: 'boost', value: 'boost' },
  { label: 'bury', value: 'bury' },
  { label: 'include', value: 'include' },
  { label: 'exclude', value: 'exclude' },
];

const allValueSources = [
  { label: 'page context', value: 'pageContext' },
  { label: "user's favorite", value: 'profileContext' },
  { label: 'manual', value: 'manual' },
];

const ProductFeedRule = ({ rule, updateRule, scope }) => {
  const valueSources = allValueSources.filter((source) => {
    if (source.value === 'pageContext' && scope !== 'web') return false;
    if (source.value === 'profileContext' && scope === 'ads') return false;
    return true;
  });

  const handleRangeValueChange = (index, prop, e) => {
    let values = [];
    if (rule[prop]) {
      values = [...rule[prop]];
    }

    values[index] = e.target.value;
    updateRule({ [prop]: values });
  };

  const handleStringValueChange = (event) => {
    let values = null;
    if (event.target.value) {
      values = event.target.value.split('|');
    }
    const changes = { values };
    if (!(values || []).includes('{{userFavorite}}')) {
      changes.userFavoriteProductsCount = null;
    }
    updateRule(changes);
  };

  const handleModeChange = (mode) => {
    updateRule({ values: [], valueMultipliers: [], mode, userFavoriteProductsCount: null });
  };

  let valueSourceSelector = '';
  let valueSource = '';
  let valueWeight = '';
  let userFavoriteProductsCount = '';

  if (rule.mode === 'boost' || rule.mode === 'bury') {
    valueWeight = (
      <>
        <Card withBorder p="xs">
          <Grid align="center">
            <Grid.Col span={2}>
              <Text size="sm">weight</Text>
            </Grid.Col>
            <Grid.Col span={10}>
              <LabeledSlider min={1} max={10} value={rule.weight} onChange={(weight) => updateRule({ weight })} />
            </Grid.Col>
          </Grid>
        </Card>
      </>
    );
  }

  if (rule.mode !== 'off' && rule.type !== 'toggle') {
    if (rule.type !== 'geoDistance') {
      valueSourceSelector = (
        <Box my="md">
          <SegmentedControl
            value={rule.valueSource ?? ''}
            onChange={(valueSource) => updateRule({ valueSource, userFavoriteProductsCount: null })}
            data={valueSources}
            aria-label="Attribute Value Source"
          />
        </Box>
      );
    }

    if (rule.valueSource === 'manual') {
      if (rule.type === 'string') {
        valueSource = (
          <>
            <TextInput
              name="value"
              value={(rule.values || []).join('|')}
              placeholder="values separated by | , e.g. firstVal|secondVal|{{pageContext}}|{{userFavorite}}|{{any}}"
              onChange={handleStringValueChange.bind(this)}
            />
            <Box size="sm" c="dimmed" mt="xs">
              You may use <strong>{'{{any}}'}</strong>, <strong>{'{{pageContext}}'}</strong> and{' '}
              <strong>{'{{userFavorite}}'}</strong> as placeholders.
            </Box>
          </>
        );
      } else if (rule.type === 'date') {
        valueSource = (
          <>
            <Grid>
              <Grid.Col span={6}>
                <DatePicker
                  prepend="from"
                  size="sm"
                  value={rule.values[0] || ''}
                  onChange={(value) => handleRangeValueChange(0, 'values', { target: { value } })}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <DatePicker
                  prepend="to"
                  size="sm"
                  value={rule.values[1] || ''}
                  onChange={(value) => handleRangeValueChange(1, 'values', { target: { value } })}
                />
              </Grid.Col>
            </Grid>
          </>
        );
      } else if (rule.type === 'numeric') {
        valueSource = (
          <>
            <Grid>
              <Grid.Col span={6}>
                <NumberInput
                  label="from"
                  size="sm"
                  name="fromValue"
                  value={rule.values[0] || ''}
                  onChange={(value) => handleRangeValueChange(0, 'values', { target: { value } })}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput
                  label="to"
                  size="sm"
                  name="toValue"
                  value={rule.values[1] || ''}
                  onChange={(value) => handleRangeValueChange(1, 'values', { target: { value } })}
                />
              </Grid.Col>
            </Grid>
          </>
        );
      } else if (rule.type === 'geoDistance') {
        valueSource = (
          <>
            <Grid>
              <Grid.Col span={12}>
                <NumberInput
                  label="within"
                  size="sm"
                  name="fromValue"
                  value={rule.values[0] || ''}
                  onChange={(value) => handleRangeValueChange(0, 'values', { target: { value } })}
                  rightSection={<Text size="sm">km</Text>}
                />
              </Grid.Col>
            </Grid>
          </>
        );
      }
    } else if (rule.type === 'numeric') {
      valueSource = (
        <>
          <Grid>
            <Grid.Col span={6}>
              <NumberInput
                label="lower bound multiplier"
                size="sm"
                name="fromValueMultiplier"
                value={rule.valueMultipliers[0] || ''}
                onChange={(value) => handleRangeValueChange(0, 'valueMultipliers', { target: { value } })}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <NumberInput
                label="upper bound multiplier"
                size="sm"
                name="fromValueMultiplier"
                value={rule.valueMultipliers[1] || ''}
                onChange={(value) => handleRangeValueChange(1, 'valueMultipliers', { target: { value } })}
              />
            </Grid.Col>
          </Grid>
        </>
      );
    }

    // `rule.type !== 'geoDistance'` is belt-and-braces: geoDistance rules always ship with
    // valueSource: 'manual' (see src/constants/recommenderRules.jsx), so this branch is
    // never actually reached for that type, but it keeps the condition self-explanatory.
    const usesUserFavorite =
      (rule.valueSource === 'profileContext' && rule.type !== 'geoDistance') ||
      (rule.valueSource === 'manual' && rule.type === 'string' && (rule.values || []).includes('{{userFavorite}}'));

    if (usesUserFavorite) {
      userFavoriteProductsCount = (
        <Grid>
          <Grid.Col span={6}>
            <NumberInput
              label="products considered"
              description="How many of the most recently viewed / added-to-cart products the favorite values are taken from. Leave empty to use all recorded products."
              size="sm"
              name="userFavoriteProductsCount"
              placeholder="all (up to 20)"
              min={1}
              max={20}
              clampBehavior="strict"
              allowDecimal={false}
              value={rule.userFavoriteProductsCount ?? ''}
              onChange={(value) => updateRule({ userFavoriteProductsCount: typeof value === 'number' ? value : null })}
            />
          </Grid.Col>
        </Grid>
      );
    }
  }

  return (
    <Grid align="flex-start" mb="sm">
      <Grid.Col span={{ base: 12, sm: 4 }}>
        <Text fw={500}>{rule.name}</Text>
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 8 }}>
        <Flex direction="column">
          <SegmentedControl
            value={rule.mode ?? ''}
            onChange={(val) => handleModeChange(val)}
            data={modes}
            aria-label="Attribute Mode"
          />
        </Flex>
        <Flex direction="column" mt="md">
          {valueSourceSelector}
        </Flex>
        <div style={{ marginTop: 'var(--mantine-spacing-md)' }}>{valueSource}</div>
        <div style={{ marginTop: 'var(--mantine-spacing-md)' }}>{userFavoriteProductsCount}</div>
        <div style={{ marginTop: 'var(--mantine-spacing-md)' }}>{valueWeight}</div>
      </Grid.Col>
    </Grid>
  );
};

export default ProductFeedRule;
